/**
 * PlanLimitPopover
 *
 * Explains a plan limit next to the control that hit it, instead of moving
 * the person somewhere else. Being yanked to a pricing page mid-task is
 * jarring and loses whatever they were doing; a small panel anchored to the
 * button keeps them in place and still offers the way forward.
 *
 * Used where a limit is genuinely the product: export, and the alert and
 * holding caps. Not for anything artificial.
 */

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Popover, Box, Typography, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import { useAuth } from '../../context/AuthContext';
import { useThemeMode } from '../../context/ThemeContext';

function PlanLimitPopover({ anchorEl, open, onClose, title, message }) {
  const { palette } = useThemeMode();
  const { isAuthenticated } = useAuth();

  const destination = isAuthenticated ? '/app/subscription' : '/signup';
  const cta = isAuthenticated ? 'See plans' : 'Create a free account';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{
        paper: {
          sx: {
            mt: 1, p: 2, maxWidth: 320, borderRadius: '12px',
            border: `1px solid ${palette.cardBorder}`,
            boxShadow: palette.shadowMd,
          },
        },
      }}
    >
      <Box sx={{ display: 'flex', gap: 1.25, mb: 1.25 }}>
        <Icon icon="ph:lock-simple" width={17} height={17} color={palette.warning} style={{ flexShrink: 0, marginTop: 2 }} />
        <Box>
          {title && (
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: palette.text, mb: 0.25 }}>
              {title}
            </Typography>
          )}
          <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary, lineHeight: 1.55 }}>
            {message}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button size="small" onClick={onClose} sx={{ textTransform: 'none', color: palette.textMuted }}>
          Not now
        </Button>
        <Button
          component={RouterLink}
          to={destination}
          size="small"
          variant="contained"
          disableElevation
          onClick={onClose}
          sx={{
            bgcolor: palette.accent, color: '#fff', fontWeight: 600,
            textTransform: 'none', borderRadius: '9px',
            '&:hover': { bgcolor: '#1C9550' },
          }}
        >
          {cta}
        </Button>
      </Box>
    </Popover>
  );
}

export default PlanLimitPopover;
